import {
  useConsumptionData,
  useRelationshipInsights,
} from "@features/cx/consumerManagement/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { BarChart } from "@shared/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Activity } from "lucide-react";
import React, { useState } from "react";
import { RelationshipInsights } from "./RelationshipInsights";

interface OverviewTabProps {
  consumerId: string;
  consumerNo: string;
  remoteUtilityId: number;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  consumerId,
  consumerNo,
  remoteUtilityId,
}) => {
  const [selectedUtility, setSelectedUtility] = useState("Water");
  const { data: consumptionData = [], error: consumptionError } =
    useConsumptionData(consumerNo, "6months", remoteUtilityId.toString());

  const { data: relationshipData } = useRelationshipInsights(
    consumerId,
    remoteUtilityId.toString()
  );

  const error = consumptionError;

  // Filter consumption data based on selected utility
  const filteredConsumptionData = Object.entries(consumptionData).map(
    ([month, consumption]) => {
      return {
        month,
        [selectedUtility]: consumption.consumption,
      };
    }
  );

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {relationshipData && (
        <RelationshipInsights
          accountAge={relationshipData.accountAgeMonths}
          serviceRequestCount={relationshipData.serviceRequestCount}
          complaintCount={relationshipData.complaintRequestCount}
          communicationPreference={relationshipData.communicationPreference}
        />
      )}

      <ConsumptionChart
        data={filteredConsumptionData}
        selectedUtility={selectedUtility}
        onUtilityChange={setSelectedUtility}
      />
    </div>
  );
};

// Sub-component for consumption chart
interface ConsumptionChartProps {
  data: any[];
  selectedUtility: string;
  onUtilityChange: (utility: string) => void;
}

const ConsumptionChart: React.FC<ConsumptionChartProps> = ({
  data,
  selectedUtility,
  onUtilityChange,
}) => {
  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Activity className="h-4 w-4 text-blue-600" />
            Consumption by Service (Last 6 Months)
          </CardTitle>
          <Select value={selectedUtility} onValueChange={onUtilityChange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Water">Water</SelectItem>
              <SelectItem value="Sewage">Sewage</SelectItem>
              <SelectItem value="Waste">Waste</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <BarChart
          data={data}
          index="month"
          categories={[selectedUtility]}
          colors={["#2563eb"]}
          valueFormatter={(value) => `${value} units`}
        />
      </CardContent>
    </Card>
  );
};
